import { EFinancingProgram } from '@if/core/types'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Avatar, IconButton, SvgIcon, Tooltip } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOptionalUser } from 'src/providers/UserProvider'
import { COLOR_TOKENS } from 'src/services/style-tokens'
import AssignFollowUpAgentDialog from './AssignFollowUpAgentDialog'

interface FollowUpAgent {
  id: string
  firstName: string
  lastName: string
  initials: string
}

interface FollowUpAgentBadgeProps {
  followUpAgent: FollowUpAgent | null | undefined
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  currentFilters?: Record<string, string | number | string[] | number[]>
}

const FollowUpAgentBadge = ({
  followUpAgent,
  creditApplicationId,
  financingProgramId,
  currentFilters,
}: FollowUpAgentBadgeProps) => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const user = useOptionalUser()
  
  const canEditOthers = user?.rights?.canAssignFollowUpAgentToOthers ?? false
  const canEditSelf = user?.rights?.canAssignFollowUpAgentToSelf ?? false
  const isAssignedOrEmpty = !followUpAgent || followUpAgent.id === user?.id
  const canEdit = canEditOthers || (canEditSelf && isAssignedOrEmpty)

  const tooltipTitle = useMemo(() => {
    if (followUpAgent) {
      return `${String(t('followUpAgent.label'))}: ${followUpAgent.firstName} ${followUpAgent.lastName}`
    }
    return canEdit
      ? String(t('followUpAgent.assignDialogTitle'))
      : String(t('followUpAgent.readOnly'))
  }, [followUpAgent, canEdit, t])

  const handleClick = () => {
    if (!canEdit) return
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
  }

  if (financingProgramId !== EFinancingProgram.Personal) {
    return null
  }

  if (!followUpAgent) {
    return (
      <>
        <Tooltip title={tooltipTitle}>
          <span>
            <IconButton
              onClick={handleClick}
              disabled={!canEdit}
              sx={{
                color: COLOR_TOKENS.addFollowUpAgentIcon,
                cursor: canEdit ? 'pointer' : 'default',
              }}
            >
              <SvgIcon fontSize="large">
                <AddCircleIcon />
              </SvgIcon>
            </IconButton>
          </span>
        </Tooltip>

        {canEdit && (
          <AssignFollowUpAgentDialog
            open={dialogOpen}
            onClose={handleDialogClose}
            currentAgent={null}
            creditApplicationId={creditApplicationId}
            financingProgramId={financingProgramId}
            currentFilters={currentFilters}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span>
          <IconButton
            onClick={handleClick}
            disabled={!canEdit}
            color="success"
            sx={{
              cursor: canEdit ? 'pointer' : 'default',
            }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'success.main', fontSize: '1rem', fontWeight: 'bold' }}>
              {followUpAgent.initials}
            </Avatar>
          </IconButton>
        </span>
      </Tooltip>

      {canEdit && (
        <AssignFollowUpAgentDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          currentAgent={followUpAgent}
          creditApplicationId={creditApplicationId}
          financingProgramId={financingProgramId}
          currentFilters={currentFilters}
        />
      )}
    </>
  )
}

export default FollowUpAgentBadge
